import type { RelationshipRole, RelationshipRoleOption } from '../hooks/usePermissionsAdministration';
import { ENFORCEMENT_LABELS } from '../lib/permissionsPresentation';

interface Props {
  roles: RelationshipRoleOption[];
  granted: (role: RelationshipRole) => string[];
  editable: boolean;
  onChange: (role: RelationshipRoleOption, capabilityKey: string, granted: boolean) => void;
}

/** What a person may do on ONE relationship (or unit) because of the role they hold on it. Never district- or council-wide. */
export default function RelationshipRoleGrants({ roles, granted, editable, onChange }: Props) {
  if (roles.length === 0) return null;
  return (
    <section className="permissions-group permissions-roles" aria-label="Relationship role permissions">
      <div className="permissions-group-head"><h3>Relationship roles</h3><span>{roles.length} roles · separate from the Position table above</span></div>
      <div className="permissions-note">
        <b>These apply only to the one relationship (or unit) where a person holds the role.</b> They never reach the rest of a district or council.
        Financial control, export, print, administration, merging and assignment management are not offered here; grant those to a Position.
      </div>
      <div className="permissions-roles-list">
        {roles.map((role) => {
          const held = new Set(granted(role.role));
          const labelOf = new Map(role.capabilities.map((capability) => [capability.key, capability.label]));
          return (
            <fieldset className="permissions-role" key={role.role} disabled={!editable}>
              <legend>{role.label}</legend>
              <p>{role.description}</p>
              <ul>
                {role.capabilities.map((capability) => {
                  const needs = capability.requires.filter((key) => labelOf.has(key)).map((key) => labelOf.get(key));
                  return (
                    <li key={capability.key} className={held.has(capability.key) ? 'granted' : ''}>
                      <label title={`${capability.description} ${capability.enforcement.note}`}>
                        <input
                          type="checkbox"
                          aria-label={`${role.label}: ${capability.label}`}
                          checked={held.has(capability.key)}
                          onChange={(event) => onChange(role, capability.key, event.target.checked)}
                        />
                        <span>{capability.label}</span>
                      </label>
                      <small className={`permissions-enforcement ${capability.enforcement.status}`}>{ENFORCEMENT_LABELS[capability.enforcement.status]}</small>
                      {needs.length > 0 && <small className="permissions-role-needs">Also turns on: {needs.join(', ')}</small>}
                    </li>
                  );
                })}
              </ul>
            </fieldset>
          );
        })}
      </div>
    </section>
  );
}
